import { useState } from 'react';
import { Car, AlertCircle, Calendar, FileText, Droplet, Wrench, Bell, CheckCircle2, XCircle } from 'lucide-react';

interface VehicleManagementScreenProps {
  onBack: () => void;
}

export function VehicleManagementScreen({ onBack }: VehicleManagementScreenProps) {
  const [fuelLog, setFuelLog] = useState({
    lastFill: '2026-01-25',
    amount: 15,
    cost: 7500,
  });

  const vehicle = {
    type: 'Motorcycle',
    make: 'Honda',
    model: 'Wave 110',
    year: 2024,
    color: 'Black',
    licensePlate: 'ABC-123-XY',
  };

  const documents = [
    {
      name: 'National ID (NIN)',
      expiry: '2027-06-15',
      status: 'valid',
      daysUntilExpiry: 503,
    },
    {
      name: 'Vehicle Registration',
      expiry: '2026-12-31',
      status: 'valid',
      daysUntilExpiry: 337,
    },
    {
      name: 'Insurance',
      expiry: '2026-03-20',
      status: 'expiring',
      daysUntilExpiry: 51,
    },
    {
      name: 'Road Worthiness',
      expiry: '2026-02-15',
      status: 'expiring',
      daysUntilExpiry: 18,
    },
  ];

  const maintenanceSchedule = [
    {
      task: 'Oil Change',
      dueDate: '2026-02-05',
      daysUntil: 8,
      completed: false,
    },
    {
      task: 'Tire Check',
      dueDate: '2026-02-10',
      daysUntil: 13,
      completed: false,
    },
    {
      task: 'Brake Inspection',
      dueDate: '2026-01-30',
      daysUntil: 2,
      completed: false,
    },
  ];

  const maintenanceHistory = [
    {
      date: '2026-01-15',
      task: 'Oil Change',
      cost: 5000,
    },
    {
      date: '2025-12-20',
      task: 'New Tires',
      cost: 25000,
    },
    {
      date: '2025-11-10',
      task: 'Battery Replacement',
      cost: 15000,
    },
  ];

  const getDocumentStatusColor = (status: string) => {
    switch (status) {
      case 'valid': return 'bg-green-50 border-green-200 text-green-700';
      case 'expiring': return 'bg-yellow-50 border-yellow-200 text-yellow-700';
      case 'expired': return 'bg-red-50 border-red-200 text-red-700';
      default: return 'bg-gray-50 border-gray-200 text-gray-700';
    }
  };

  const getDocumentIcon = (status: string) => {
    switch (status) {
      case 'valid': return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case 'expiring': return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      case 'expired': return <XCircle className="w-5 h-5 text-red-600" />;
      default: return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  return (
    <div className="absolute inset-0 bg-white flex flex-col">
      {/* Header */}
      <div className="p-8 pt-16 bg-gradient-to-b from-[#E0001B]/5 to-transparent">
        <button
          onClick={onBack}
          className="mb-6 text-[#E0001B] hover:underline"
        >
          ← Back
        </button>

        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-full bg-[#E0001B]/10 flex items-center justify-center">
            <Car className="w-8 h-8 text-[#E0001B]" />
          </div>
          <div>
            <h1 className="text-[28px] text-[#111111] mb-1">
              Vehicle Management
            </h1>
            <p className="text-[#6B6B6B]">Manage your vehicle & documents</p>
          </div>
        </div>

        {/* Vehicle Info Card */}
        <div className="p-6 bg-white rounded-[16px] shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-[20px] font-bold text-[#111111]">
                {vehicle.make} {vehicle.model}
              </p>
              <p className="text-[14px] text-[#6B6B6B]">{vehicle.year} • {vehicle.color}</p>
            </div>
            <div className="text-right">
              <p className="text-[14px] text-[#6B6B6B]">License Plate</p>
              <p className="text-[18px] font-bold text-[#111111]">{vehicle.licensePlate}</p>
            </div>
          </div>
          <button className="w-full h-10 rounded-[12px] bg-gray-100 text-[#111111] hover:bg-gray-200 transition-colors text-[14px]">
            Switch Fleet Type
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-8 py-6">
        {/* Document Status */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[18px] font-medium">Document Status</h2>
            <Bell className="w-5 h-5 text-[#6B6B6B]" />
          </div>
          <div className="space-y-3">
            {documents.map((doc, index) => (
              <div
                key={index}
                className={`p-4 rounded-[12px] border ${getDocumentStatusColor(doc.status)}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getDocumentIcon(doc.status)}
                    <div>
                      <p className="text-[16px] font-medium">{doc.name}</p>
                      <p className="text-[12px] opacity-80">
                        Expires: {new Date(doc.expiry).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[14px] font-medium">
                      {doc.daysUntilExpiry} days
                    </p>
                    <p className="text-[12px] opacity-80 capitalize">{doc.status}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Fuel Tracking */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Droplet className="w-5 h-5 text-[#E0001B]" />
            <h2 className="text-[18px] font-medium">Fuel Tracking</h2>
          </div>
          <div className="p-6 bg-blue-50 rounded-[12px] border border-blue-100">
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <p className="text-[24px] font-bold text-blue-600">{fuelLog.amount}L</p>
                <p className="text-[12px] text-blue-700">Last Fill</p>
              </div>
              <div className="text-center">
                <p className="text-[24px] font-bold text-blue-600">₦{fuelLog.cost.toLocaleString()}</p>
                <p className="text-[12px] text-blue-700">Cost</p>
              </div>
              <div className="text-center">
                <p className="text-[24px] font-bold text-blue-600">3</p>
                <p className="text-[12px] text-blue-700">Days Ago</p>
              </div>
            </div>
            <button className="w-full h-10 rounded-[12px] bg-blue-600 text-white hover:bg-blue-700 transition-colors text-[14px]">
              Log Fuel Fill-Up
            </button>
          </div>
        </div>

        {/* Maintenance Schedule */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Wrench className="w-5 h-5 text-[#E0001B]" />
            <h2 className="text-[18px] font-medium">Maintenance Schedule</h2>
          </div>
          <div className="space-y-3">
            {maintenanceSchedule.map((item, index) => (
              <div
                key={index}
                className={`p-4 rounded-[12px] border-2 ${
                  item.daysUntil <= 7
                    ? 'bg-red-50 border-red-200'
                    : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <Calendar className={`w-5 h-5 ${item.daysUntil <= 7 ? 'text-red-600' : 'text-[#6B6B6B]'}`} />
                    <div>
                      <p className="text-[16px] font-medium">{item.task}</p>
                      <p className="text-[12px] text-[#6B6B6B]">
                        Due: {new Date(item.dueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-[14px] font-medium ${item.daysUntil <= 7 ? 'text-red-600' : 'text-[#111111]'}`}>
                      {item.daysUntil} days
                    </p>
                  </div>
                </div>
                <button className="w-full h-8 rounded-[12px] bg-[#00B277] text-white hover:bg-[#009963] transition-colors text-[12px]">
                  Mark as Complete
                </button>
              </div>
            ))}
          </div>
          <button className="w-full mt-3 h-10 rounded-[12px] bg-gray-100 text-[#111111] hover:bg-gray-200 transition-colors text-[14px]">
            + Add Maintenance Task
          </button>
        </div>

        {/* Maintenance History */}
        <div className="mb-6">
          <h2 className="text-[18px] font-medium mb-4">Maintenance History</h2>
          <div className="space-y-2">
            {maintenanceHistory.map((item, index) => (
              <div
                key={index}
                className="p-4 bg-gray-50 rounded-[12px] flex items-center justify-between"
              >
                <div>
                  <p className="text-[14px] font-medium">{item.task}</p>
                  <p className="text-[12px] text-[#6B6B6B]">
                    {new Date(item.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </p>
                </div>
                <p className="text-[16px] font-bold text-[#E0001B]">
                  ₦{item.cost.toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Tips */}
        <div className="p-6 bg-blue-50 rounded-[12px] border border-blue-100">
          <h3 className="text-[16px] font-medium text-blue-900 mb-3">💡 Vehicle Care Tips</h3>
          <ul className="text-[14px] text-blue-800 space-y-2">
            <li>• Check tire pressure weekly for better fuel efficiency</li>
            <li>• Keep your documents updated to avoid penalties</li>
            <li>• Regular maintenance extends vehicle lifespan</li>
            <li>• Track fuel consumption to monitor performance</li>
            <li>• Set reminders for insurance and registration renewals</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
